import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const inputCls =
  "h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-medium text-ink outline-none transition-colors duration-200 focus:border-royal";

export default function ResetPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password needs at least 6 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "That reset link didn't work — request a new one");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="bg-paper py-24" data-testid="reset-page">
      <div className="mx-auto max-w-md px-6">
        <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
          {done ? (
            <div className="text-center">
              <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
              <h1 className="mt-4 font-display text-2xl font-bold text-ink">Password updated</h1>
              <p className="mt-2 text-sm text-slate-500">You're all set — sign in with your new password.</p>
              <Link
                to="/auth"
                data-testid="reset-signin-link"
                className="mt-6 inline-block rounded-full bg-royal px-8 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-royal-dark"
              >
                Sign in
              </Link>
            </div>
          ) : !token ? (
            <div className="text-center">
              <h1 className="font-display text-2xl font-bold text-ink">That link is incomplete</h1>
              <p className="mt-2 text-sm text-slate-500">Request a fresh reset link from the sign-in page.</p>
              <Link to="/auth" className="mt-6 inline-block text-sm font-semibold text-royal hover:underline">
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-royal-light text-royal">
                  <KeyRound className="h-5 w-5" />
                </span>
                <div>
                  <h1 className="font-display text-xl font-bold text-ink">Choose a new password</h1>
                  <p className="text-xs text-slate-500">For your Oregon Tote Rentals account</p>
                </div>
              </div>
              <form onSubmit={submit} className="mt-6 space-y-3" data-testid="reset-form">
                <input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type="password"
                  placeholder="New password (6+ characters)"
                  data-testid="reset-password-input"
                  className={inputCls}
                  required
                />
                <input
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  type="password"
                  placeholder="Confirm new password"
                  data-testid="reset-confirm-input"
                  className={inputCls}
                  required
                />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="reset-submit-button"
                  className="w-full rounded-xl bg-royal py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-royal-dark disabled:opacity-50"
                >
                  {busy ? "Updating…" : "Update password"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
